import React from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  ResponsiveContainer,
  ReferenceDot,
  PieChart,
  Pie,
  Cell,
  Customized
} from 'recharts'
import { buildBellCurveData } from '../../services/utils'

const Results: React.FC<{ score: number, percentile: number, scores: number[], maxPoints?: number }> = ({ score, percentile, scores, maxPoints = 50 }) => {
  const testScoreData = buildBellCurveData(scores, maxPoints, maxPoints + 1)

  // X-axis ticks including the user's percentile
  let xTicks = Array.from({length: 6}, (_, i) => Math.round((maxPoints / 5) * i));

  if (!xTicks.includes(score)) {
    xTicks.push(score)
  }

  xTicks.sort((a, b) => a - b)

  const completion = Math.round(score / maxPoints * 100)

  const pieData = [
    { name: 'done', value: completion },
    { name: 'rest', value: 100 - completion }
  ]

  // Text based on user results
  const resultText = (value: number) => {
    if (value >= 75) return 'Skvělý výsledek, drž tuhle formu!'
    if (value >= 50) return 'Dobře ty, pěkná práce!'
    if (value >= 25) return 'Není to špatný, ale ještě trochu zamakáme!'
    return 'Musíš ještě zamakat, ale nevzdávej to!'
  }

  return (
    <>
      <div className="grid grid-cols-10 gap-4 text-white mb-5">
        <div className="col-span-12 lg:col-span-3">
          <h3>Tvůj výsledek</h3>
          <div className="bg-[#A8CD00] rounded-xl px-4 py-3 flex items-start lg:justify-between min-h-[153px]">
            <div className="flex flex-col items-center mb-2 me-2">
              <PieChart width={80} height={80}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  outerRadius={36}
                  isAnimationActive={false}
                  stroke="none"
                  strokeWidth={0}
                >
                  <Cell fill="#FFFFFF" stroke="none" />
                  <Cell fill="transparent" stroke="none" />
                </Pie>
                <Customized
                  component={({ width, height }: any) => (
                    <circle
                      cx={width / 2}
                      cy={height / 2}
                      r={39}
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                    />
                  )}
                />
              </PieChart>
            </div>
            <div>
              <h3 className="text-white text-[46px] mb-0 leading-none">{score} bodů</h3>
              <h3 className="text-white text-[28px] mb-2 leading-none">z {maxPoints} možných.</h3>
              <p className="text-white text-[17px] mb-0 leading-tight">{resultText(completion)}</p>
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-2">
          <h3>Percentil</h3>
          <div className="bg-[#00A0E3] rounded-xl p-2 min-h-[153px]">
            <p className="text-white text-[17px] mb-0 leading-tight">
              Jsi lepší než {percentile} % ostatních studentů
            </p>
            <h3 className="text-white block text-[67px] mb-0 leading-none">{percentile} %</h3>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <h3>Jak jsi na tom v porovnání s ostatními</h3>
          <div className="rounded-xl border border-[#DFDFE4] p-2">
            <ResponsiveContainer width="100%" height={135}>
              <AreaChart data={testScoreData} margin={{ top: 24, right: 0, left: 0, bottom: 0 }}>
                {/* Vertical split gradient at score */}
                <defs>
                  <linearGradient id="percentileSplit" x1="0" y1="0" x2="1" y2="0">
                    <stop offset={`${score * 100 / maxPoints}%`} stopColor="#A8CD00" />
                    {/* duplicate stop to create a hard edge */}
                    <stop offset={`${score * 100 / maxPoints}%`} stopColor="#00A0E3" />
                    <stop offset="100%" stopColor="#00A0E3" />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="points"
                  type="number"
                  domain={[0, maxPoints]}
                  padding={{ left: 10, right: 10 }}
                  tickMargin={2}
                  ticks={xTicks}
                  tick={{ fontSize: 14, fill: '#00A0E3' }}
                  axisLine={{ stroke: '#00A0E3' }}
                  tickLine={{ stroke: '#00A0E3' }}
                />
                {/* Single area with gradient fill split */}
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="none"
                  fill="url(#percentileSplit)"
                  fillOpacity={1}
                  isAnimationActive={false}
                  dot={false}
                  activeDot={false}
                />

                {/* User score marker */}
                <ReferenceDot
                  x={score}
                  y={
                    testScoreData[
                      Math.min(Math.max(0, Math.round(score)), testScoreData.length - 1)
                    ]?.users ?? 0
                  }
                  r={6}
                  fill="#A8CD00"
                  stroke="#fff"
                  strokeWidth={2}
                  isFront
                  ifOverflow="visible"
                  label={{
                    value: `${percentile}%`,
                    position: 'top',
                    fill: '#A8CD00',
                    fontWeight: 'bold',
                    fontSize: 14
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  )
}

export default Results
